import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { signIn, signUp, confirmSignUp as amplifyConfirmSignUp, signOut, getCurrentUser, fetchUserAttributes, confirmResetPassword, updatePassword, fetchAuthSession, resetPassword } from 'aws-amplify/auth';

interface User {
  id: string;
  username: string;
  email: string;
  givenName?: string;
  familyName?: string;
  name?: string;
  avatarUrl?: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (usernameOrEmail: string, password: string) => Promise<void>;
  signup: (userData: SignupData) => Promise<void>;
  confirmSignUp: (username: string, confirmationCode: string) => Promise<void>;
  logout: () => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  resetPassword: (email: string, code: string, newPassword: string) => Promise<void>;
  changePassword: (oldPassword: string, newPassword: string) => Promise<void>;
  clearError: () => void;
  error: string | null;
}

interface SignupData {
  username: string;
  email: string;
  password: string;
  givenName: string;
  familyName: string;
  gender?: string;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Check authentication state on mount
  useEffect(() => {
    checkAuthState();
  }, []);

  const checkAuthState = async () => {
    try {
      const currentUser = await getCurrentUser();
      const userAttributes = await fetchUserAttributes();
      
      // Extract JWT token from Amplify session
      const session = await fetchAuthSession();
      const accessToken = session.tokens?.accessToken?.toString();
      
      if (accessToken) {
        localStorage.setItem('authToken', accessToken);
      }
      
      const userData: User = {
        id: currentUser.userId,
        username: currentUser.username,
        email: userAttributes.email || '',
        givenName: userAttributes.given_name || '',
        familyName: userAttributes.family_name || '',
        name: userAttributes.name || `${userAttributes.given_name || ''} ${userAttributes.family_name || ''}`.trim(),
        avatarUrl: userAttributes.picture || undefined
      };
      
      setUser(userData);
      setIsAuthenticated(true);
      // Initialize minimal user row on backend (no-op if exists)
      try {
        await fetch(`${(import.meta as any).env?.VITE_API_BASE_URL || 'http://localhost:8000'}/v0_1/user/available-analytics`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
          },
        });
      } catch {}
    } catch (error: any) {
      // UserUnAuthenticatedException is expected when no user is logged in
      if (error.name === 'UserUnAuthenticatedException') {
        setUser(null);
        setIsAuthenticated(false);
        localStorage.removeItem('authToken');
      } else {
        console.error('Error checking auth state:', error);
        setUser(null);
        setIsAuthenticated(false);
        localStorage.removeItem('authToken');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (usernameOrEmail: string, password: string) => {
    try {
      setError(null);
      setIsLoading(true);
      
      // Try to sign in with the provided value (could be username or email)
      await signIn({ username: usernameOrEmail, password });
      
      // Get user data after successful sign in
      const currentUser = await getCurrentUser();
      const userAttributes = await fetchUserAttributes();
      
      // Extract JWT token from Amplify session
      const session = await fetchAuthSession();
      const accessToken = session.tokens?.accessToken?.toString();
      
      if (accessToken) {
        localStorage.setItem('authToken', accessToken);
      }
      
      const userData: User = {
        id: currentUser.userId,
        username: currentUser.username,
        email: userAttributes.email || '',
        givenName: userAttributes.given_name || '',
        familyName: userAttributes.family_name || '',
        name: userAttributes.name || `${userAttributes.given_name || ''} ${userAttributes.family_name || ''}`.trim(),
        avatarUrl: userAttributes.picture || undefined
      };
      
      setUser(userData);
      setIsAuthenticated(true);
      try {
        await fetch(`${(import.meta as any).env?.VITE_API_BASE_URL || 'http://localhost:8000'}/v0_1/user/available-analytics`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
          },
        });
      } catch {}
    } catch (error: any) {
      console.error('Login error:', error);
      setError(error.message || 'Invalid username or password');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const signup = async (userData: SignupData) => {
    try {
  
      setError(null);
      setIsLoading(true);
      
      await signUp({
        username: userData.username,
        password: userData.password,
        options: {
          userAttributes: {
            email: userData.email,
            given_name: userData.givenName,
            family_name: userData.familyName,
            name: `${userData.givenName} ${userData.familyName}`,
            gender: userData.gender
          }
        }
      });
      
      
      
      // Note: User will need to confirm their email before they can sign in
      // We don't automatically sign them in after signup
    } catch (error: any) {
      console.error('Signup error:', error);
      setError(error.message || 'Failed to create account');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      await signOut();
      setUser(null);
      setIsAuthenticated(false);
      localStorage.removeItem('authToken');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const forgotPassword = async (email: string) => {
    try {
      setError(null);
      setIsLoading(true);
      // @ts-ignore - AWS Amplify v6 API
      await resetPassword({ username: email });
    } catch (error: any) {
      console.error('Forgot password error:', error);
      setError(error.message || 'Failed to send reset email');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const resetPassword = async (email: string, code: string, newPassword: string) => {
    try {
      setError(null);
      setIsLoading(true);
      // @ts-ignore - AWS Amplify v6 API
      await confirmResetPassword({ username: email, confirmationCode: code, newPassword });
    } catch (error: any) {
      console.error('Reset password error:', error);
      setError(error.message || 'Failed to reset password');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const clearError = () => {
    setError(null);
  };

  const confirmSignUp = async (username: string, confirmationCode: string) => {
    try {
      setError(null);
      setIsLoading(true);
      
      // @ts-ignore - AWS Amplify v6 API
      await amplifyConfirmSignUp({ username, confirmationCode });
      
      // Check if user is automatically signed in after confirmation
      try {
        const currentUser = await getCurrentUser();
        const userAttributes = await fetchUserAttributes();
        const session = await fetchAuthSession();
        const accessToken = session.tokens?.accessToken?.toString();
        
        if (accessToken) {
          localStorage.setItem('authToken', accessToken);
          
          const userData: User = {
            id: currentUser.userId,
            username: currentUser.username,
            email: userAttributes.email || '',
            givenName: userAttributes.given_name || '',
            familyName: userAttributes.family_name || '',
            name: userAttributes.name || `${userAttributes.given_name || ''} ${userAttributes.family_name || ''}`.trim(),
            avatarUrl: userAttributes.picture || undefined
          };
          
          setUser(userData);
          setIsAuthenticated(true);
        }
      } catch {
        // User not automatically signed in after confirmation, which is normal
      }
      
    } catch (error: any) {
      console.error('Confirm signup error:', error);
      setError(error.message || 'Failed to confirm signup');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const changePassword = async (oldPassword: string, newPassword: string) => {
    try {
      setError(null);
      setIsLoading(true);
      
      await updatePassword({ oldPassword, newPassword });
    } catch (error: any) {
      console.error('Change password error:', error);
      setError(error.message || 'Failed to change password');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const value: AuthContextType = {
    user,
    isAuthenticated,
    isLoading,
    login,
    signup,
    confirmSignUp,
    logout,
    forgotPassword,
    resetPassword,
    changePassword,
    clearError,
    error
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 